/**
 * Payroll Periods — list + create
 * Authority: HR_PAYROLL_VIEW (read) / HR_PAYROLL_MANAGE (create)
 *
 * Endpoints:
 *   GET  /periods?status&year&page&size
 *   POST /periods?year&month
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Plus } from "lucide-react";
import { useAuth } from "#/context/AuthProvider";
import { payrollApi } from "../api";
import type { PeriodListParams } from "../api";
import { useAsyncData, useMutation, usePagination } from "../hooks";
import { monthLabel } from "../types";
import {
  ActiveFilterBadges, Btn, EmptyState, FilterPanel, PageHeader, Pagination,
  PayrollError, PayrollLoading, PeriodStatusBadge, SelectFilter,
  Table, TBody, TD, TH, THead, TR, fmtDateTime,
} from "../components/PayrollUI";

const STATUS_OPTS = [
  { label: "Draft",             value: "DRAFT" },
  { label: "Imported",          value: "IMPORTED" },
  { label: "Calculated",        value: "CALCULATED" },
  { label: "Pending Approval",  value: "PENDING_APPROVAL" },
  { label: "Approved",          value: "APPROVED" },
  { label: "Rejected",          value: "REJECTED" },
  { label: "Paid",              value: "PAID" },
];

const now = new Date();

export default function PayrollPeriodsPage() {
  const navigate = useNavigate();
  const { hasRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  const canManage = isAdmin || hasPermission("HR_PAYROLL_MANAGE");

  const pagination = usePagination(0, 12);
  const [filters, setFilters] = useState<PeriodListParams>({});
  const [showCreate, setShowCreate] = useState(false);
  const [newYear, setNewYear] = useState(now.getFullYear());
  const [newMonth, setNewMonth] = useState(now.getMonth() + 1);

  const { data, loading, error, reload } = useAsyncData(
    () => payrollApi.listPeriods(pagination.page, pagination.size, filters),
    [pagination.page, pagination.size, filters],
  );

  const create = useMutation((payload: { year: number; month: number }) =>
    payrollApi.createPeriod(payload.year, payload.month),
  );

  function setF<K extends keyof PeriodListParams>(k: K, v: PeriodListParams[K]) {
    setFilters((f) => ({ ...f, [k]: v || undefined }));
    pagination.reset();
  }

  async function doCreate() {
    const period = await create.mutate({ year: newYear, month: newMonth });
    setShowCreate(false);
    navigate(`/payroll/periods/${period.id}`);
  }

  const activeBadges = [
    filters.status && { label: `Status: ${filters.status}`, onRemove: () => setF("status", undefined) },
    filters.year && { label: `Year: ${filters.year}`, onRemove: () => setF("year", undefined) },
  ].filter(Boolean) as { label: string; onRemove: () => void }[];

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Finance"
        title="Finance Periods"
        actions={canManage && (
          <Btn variant="primary" icon={Plus} onClick={() => setShowCreate((s) => !s)}>New Period</Btn>
        )}
      />

      {showCreate && canManage && (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4">
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Year
            <input type="number" className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500"
              value={newYear} onChange={(e) => setNewYear(Number(e.target.value))} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
            Month
            <select className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500"
              value={newMonth} onChange={(e) => setNewMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{monthLabel(m)}</option>
              ))}
            </select>
          </label>
          {create.error && <p className="text-xs text-red-600">{create.error}</p>}
          <div className="flex gap-2">
            <Btn variant="primary" loading={create.loading} loadingText="Creating…" onClick={doCreate}>Create Period</Btn>
            <Btn onClick={() => setShowCreate(false)}>Cancel</Btn>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <FilterPanel activeCount={activeBadges.length} onClear={() => { setFilters({}); pagination.reset(); }} onRefresh={reload}>
          <SelectFilter label="All Statuses" value={filters.status ?? ""} onChange={(v) => setF("status", v)} options={STATUS_OPTS} />
          <input type="number" placeholder="Year" className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500"
            value={filters.year ?? ""} onChange={(e) => setF("year", e.target.value ? Number(e.target.value) : undefined)} />
        </FilterPanel>
        <ActiveFilterBadges filters={activeBadges} />
      </div>

      {loading && <PayrollLoading label="Loading finance periods…" />}
      {error && <PayrollError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Table>
            <THead>
              <TH>Period</TH><TH>Status</TH><TH>Prepared By</TH>
              <TH>Calculated</TH><TH>Submitted</TH><TH>Approved</TH><TH>Paid</TH><TH right>Actions</TH>
            </THead>
            <TBody>
              {data.content.length === 0 ? (
                <tr><td colSpan={8}><EmptyState icon={CalendarDays} title="No finance periods" description="Create a period to get started." /></td></tr>
              ) : data.content.map((p) => (
                <TR key={p.id} className="cursor-pointer" >
                  <TD onClick={() => navigate(`/payroll/periods/${p.id}`)}>
                    <div className="font-semibold text-slate-800">{monthLabel(p.periodMonth)} {p.periodYear}</div>
                    <div className="font-mono text-xs text-slate-400">{p.periodCode}</div>
                  </TD>
                  <TD><PeriodStatusBadge value={p.status} /></TD>
                  <TD muted>{p.preparedByUsername ?? "—"}</TD>
                  <TD muted>{fmtDateTime(p.calculatedAt)}</TD>
                  <TD muted>{fmtDateTime(p.submittedAt)}</TD>
                  <TD muted>{fmtDateTime(p.approvedAt)}</TD>
                  <TD muted>{fmtDateTime(p.paidAt)}</TD>
                  <TD right>
                    <Btn size="sm" onClick={() => navigate(`/payroll/periods/${p.id}`)}>Open</Btn>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}
    </div>
  );
}
